import pako from 'pako';
import { API } from 'aws-amplify';

export const isBase64 = (str) => str?.length % 4 === 0 && /^[A-Za-z0-9+/]+={0,2}$/.test(str);

// Normalise whatever the transport handed us into bytes we can inspect, without copying when
// possible. Returns null for anything that is not binary (e.g. a plain string body).
const toBytes = (data) => {
        if (data instanceof Uint8Array) return data;
        if (data instanceof ArrayBuffer) return new Uint8Array(data);
        if (data?.buffer instanceof ArrayBuffer) {
                return new Uint8Array(data.buffer, data.byteOffset ?? 0, data.byteLength);
        }
        return null;
}

// gzip streams always start with the magic bytes 0x1f 0x8b.
export const isGzip = (data) => {
        const bytes = toBytes(data);
        return !!bytes && bytes.length > 1 && bytes[0] === 0x1f && bytes[1] === 0x8b;
}

export const decodeResponse = async (result) => {
        const arrayBuffer = result instanceof Blob ? await result.arrayBuffer() : result;
        // A compressed response arrives as RAW gzip bytes (compressionMiddleware does
        // res.send(gzipSync(...)) with Content-Type application/octet-stream for any payload
        // >= 2000 bytes). Text-decoding those bytes replaces them with U+FFFD and destroys the
        // payload irrecoverably, after which isBase64() is false and JSON.parse throws — which is
        // how the public Vehicle Photo Log link page ended up with an undefined response body.
        // Ungzip before any text decoding. Non-gzip input is text-decoded exactly as before, so
        // the base64 and plain-JSON paths are unaffected.
        if (isGzip(arrayBuffer)) {
                return pako.ungzip(toBytes(arrayBuffer), { to: 'string' });
        }
        return new TextDecoder().decode(arrayBuffer)
}

export const decompress = (result)=>{
        try {
                if(isBase64(result)){
                        const binaryData = Uint8Array.from(atob(result), c => c.charCodeAt(0));
                        result = pako.ungzip(binaryData, { to: 'string' });
                }
                return JSON.parse(result)
        } catch(e){
                console.warn("Decompression or parsing failed, returning original data.", e)
                return result
        }
}

export const apiGetCompressed = async (apiName, path) => {
        const response = await API.get(apiName, path, {
                responseType: 'arraybuffer',
                response: true
        });
        const decoded = await decodeResponse(response?.data ?? response);
        const result = decompress(decoded);
        return result;
}

export const apiPostCompressed = async (apiName, path, post) => {

        post.body.compressHeavyResponses = 1;
        const jsonString = JSON.stringify(post.body)

        const sendPayloadCompressed = jsonString.length >= 2000;

        const postPayload = {
                responseType: 'arraybuffer',
                response: true,
                headers: {}
        }

        if(sendPayloadCompressed){
                const compressed = pako.gzip(new TextEncoder().encode(jsonString))
                const compressedBase64 = btoa(Array.from(compressed, b => String.fromCharCode(b)).join(''));
                postPayload.headers['Content-Type'] = 'application/octet-stream'
                postPayload.body = compressedBase64
        }else{
                postPayload.headers['Content-Type'] = 'application/json'
                postPayload.body = post.body
        }
        
        const response = await safeFunction(API.post)(apiName, path, postPayload)
        const decoded = await decodeResponse(response?.data ?? response)
        const result = decompress(decoded)
        return result
}